
import React from 'react';
import { NotificationPreferences } from '@/contexts/auth/types';
import { NotificationDay, NotificationFrequency } from '@/contexts/auth/enums';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Clock, Calendar } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

interface FrequencySectionProps {
  preferences: NotificationPreferences;
  onChange: (preferences: NotificationPreferences) => void;
}

const FrequencySection: React.FC<FrequencySectionProps> = ({ preferences, onChange }) => {
  const handleFrequencyChange = (frequency: NotificationFrequency) => {
    onChange({ ...preferences, frequency });
  };

  const handleNotificationDayChange = (day: NotificationDay) => {
    onChange({ ...preferences, notificationDay: day });
  };

  const handleNotificationTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...preferences, notificationTime: e.target.value });
  };

  const weekDays = [
    { value: NotificationDay.MONDAY, label: 'Hétfő' },
    { value: NotificationDay.TUESDAY, label: 'Kedd' },
    { value: NotificationDay.WEDNESDAY, label: 'Szerda' },
    { value: NotificationDay.THURSDAY, label: 'Csütörtök' },
    { value: NotificationDay.FRIDAY, label: 'Péntek' },
    { value: NotificationDay.SATURDAY, label: 'Szombat' },
    { value: NotificationDay.SUNDAY, label: 'Vasárnap' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-8">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <Label className="font-medium">Értesítések gyakorisága</Label>
      </div>

      <RadioGroup
        value={preferences.frequency}
        onValueChange={(value) => handleFrequencyChange(value as NotificationFrequency)}
        className="flex flex-col space-y-2"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value={NotificationFrequency.MONTHLY} id={NotificationFrequency.MONTHLY} />
          <Label htmlFor={NotificationFrequency.MONTHLY}>Havi összefoglaló (alapértelmezett)</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value={NotificationFrequency.WEEKLY} id={NotificationFrequency.WEEKLY} />
          <Label htmlFor={NotificationFrequency.WEEKLY}>Heti összefoglaló</Label>
        </div>
        {preferences.frequency === NotificationFrequency.WEEKLY && (
          <div className="ml-6 space-y-4 border-l-2 border-gray-100 pl-4 mt-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="notification-day" className="text-sm">A hét melyik napján?</Label>
              </div>
              <Select
                value={preferences.notificationDay || NotificationDay.MONDAY}
                onValueChange={(value) => handleNotificationDayChange(value as NotificationDay)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Válassz napot" />
                </SelectTrigger>
                <SelectContent>
                  {weekDays.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="notification-time" className="text-sm">Körülbelül hány órakor?</Label>
              </div>
              <Input
                id="notification-time"
                type="time"
                value={preferences.notificationTime || "10:00"}
                onChange={handleNotificationTimeChange}
              />
            </div>
          </div>
        )}
        {/* 
        <div className="flex items-center space-x-2">
          <RadioGroupItem value={NotificationFrequency.DAILY} id={NotificationFrequency.DAILY} />
          <Label htmlFor={NotificationFrequency.DAILY}>Napi összefoglaló</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value={NotificationFrequency.HOURLY} id={NotificationFrequency.HOURLY} />
          <Label htmlFor={NotificationFrequency.HOURLY}>Óránkénti összefoglaló</Label>
        </div> */}
      </RadioGroup>

    </div>
  );
};

export default FrequencySection;
